import React from 'react';
import { Form, Input, Select, DatePicker, Button, Space, notification } from 'antd';
import { SearchOutlined, ClearOutlined, DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import styles from './FilterPanel.module.css';
import { useLoaderProvider } from '../../contexts/LoaderContext';

const { Option } = Select;
const { RangePicker } = DatePicker;

export interface FilterConfig {
  name: string;
  label: string;
  type: 'input' | 'select' | 'date' | 'dateRange';
  options?: { value: string; label: string }[];
}

interface FilterPanelProps {
  filters: FilterConfig[];
  onFilterChange: (filters: Record<string, string>) => void;
  showExport?: boolean;
  exportService?: (filters: Record<string, string>) => Promise<Blob>;
  exportFileName?: string;
}

const FilterPanel: React.FC<FilterPanelProps> = ({ 
  filters, 
  onFilterChange, 
  showExport = false, 
  exportService,
  exportFileName = 'export.csv'
}) => {
  const [form] = Form.useForm();
  const { showLoader, hideLoader } = useLoaderProvider();

  const handleFilter = (values: Record<string, any>) => {
    const formattedValues = formatFilterValues(values);
    onFilterChange(formattedValues);
  };

  const handleExport = async () => {
    if (!exportService) {
      notification.error({ message: "Servizio di esportazione non fornito" });
      return;
    }

    try {
      const values = form.getFieldsValue();
      const formattedValues = formatFilterValues(values);
      showLoader("Export...");
      const response = await exportService(formattedValues);
      
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', exportFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      notification.success({ message: "Esportazione completata con successo" });
    } catch (error) {
      console.error("Errore durante l'esportazione:", error);
      notification.error({ message: "Errore durante l'esportazione" });
    } finally {
      hideLoader();
    }
  };

  const formatFilterValues = (values: Record<string, any>): Record<string, string> => {
    return Object.entries(values).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null) {
        if (moment.isMoment(value)) {
          acc[key] = value.toISOString();
        } else if (Array.isArray(value) && value.length === 2) {
          acc[`${key}.greaterThan`] = value[0].startOf('day').toISOString();
          acc[`${key}.lessThan`] = value[1].endOf('day').toISOString();
        } else {
          acc[key] = String(value);
        }
      }
      return acc;
    }, {} as Record<string, string>);
  };

  const handleReset = () => {
    form.resetFields();
    onFilterChange({});
  };

  const renderFilterField = (filter: FilterConfig) => {
    switch (filter.type) {
      case 'input':
        return <Input placeholder={`Inserisci ${filter.label}`} />;
      case 'select':
        return (
          <Select placeholder={`Seleziona ${filter.label}`}>
            {filter.options?.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        );
      case 'date':
        return <DatePicker style={{ width: '100%' }} />;
      case 'dateRange':
        return <RangePicker style={{ width: '100%' }} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.filterPanel}>
      <Form form={form} layout="vertical" onFinish={handleFilter}>
        <div className={styles.filterGrid}>
          {filters.map((filter) => (
            <Form.Item key={filter.name} name={filter.name} label={filter.label}>
              {renderFilterField(filter)}
            </Form.Item>
          ))}
        </div>
        <div className={styles.buttonGroup}>
          <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
            Applica Filtri
          </Button>
          <Button onClick={handleReset} icon={<ClearOutlined />}>
            Resetta Filtri
          </Button>
          {showExport && (
            <Button onClick={handleExport} icon={<DownloadOutlined />}>
              Esporta
            </Button>
          )}
        </div>
      </Form>
    </div>
  );
};

export default FilterPanel;